import React, { CSSProperties } from 'react';

import { makeClassNames } from '../../../utils/styles';
import PlayerCardFooter from '../../atoms/player-card-footer/player-card-footer';
import { sportsDetailCardtypes, ICardsIntcmp } from '../../../typings/sports-detail';
import Text from '../../atoms/text/text';
import ImgPlaceholder from '../../atoms/img-placeholder/img-placeholder';
import Cta from '../../atoms/cta/cta';
import t from '../../../labels/labelling-utils';
import './sports-detail-card.scss';

export interface ISportsDetailCard {
  type: sportsDetailCardtypes;
  imageUrl?: string;
  firstName?: string;
  lastName: string;
  url?: string;
  mvp?: boolean;
  logoUrl: string;
  teamName: string;
  backgroundColor?: string;
  points?: number;
  showPoints: boolean;
  role?: string;
  position?: number;
  teamUrl?: string;
  label?: string;
  lazy?: boolean;
  trackingDataValues?: ICardsIntcmp;
}

const athletePlaceholderLight =
  'https://static.sky.it/images/skysport/configurations/placeholders/football/SkySpport-player_placeholder.png';
const athletePlaceholderDark =
  'https://static.sky.it/images/skysport/configurations/placeholders/motorsports/SkySpport--motorsports_placeholder-dark.png';

const carPlaceholderDark =
  'https://static.sky.it/images/skysport/configurations/placeholders/motorsports/SkySpport-motorsports_placeholder-car.png';

const defaultBackgroundColor = '#d6d6d6';

const classNames = (params: { type: sportsDetailCardtypes }) => {
  const { type } = params;

  let prefix = 'player';
  if (type === sportsDetailCardtypes.DRIVERS) {
    prefix = 'driver';
  }
  if (type === sportsDetailCardtypes.TEAMS) {
    prefix = 'team';
  }

  return makeClassNames({
    player: `${prefix}-card__${prefix}`,
    playerHeaderLeft: [`${prefix}-card__header`, `${prefix}-card__header--left`],
    playerHeaderRight: [`${prefix}-card__header`, `${prefix}-card__header--right`],
    image: `${prefix}-card__image`,
    wrapper: `${prefix}-card__wrapper`,
    fullLink: `${prefix}-card__full-link`,
    playerCardPosition: `${prefix}-card-position`,
    playerCardPositionLabel: `${prefix}-card-position__label`,
    item: [`${prefix}s-card`, `${prefix}s-card--has-head-shot`, `${prefix}-card__${prefix}--full-link`],
    playerBadge: 'player-report-card__badge',
    team: ['card-team', `${prefix}-card__team`],
  });
};

const PlayerCardPosition = (props: { position: number; className: string; labelClassName: string }) => {
  const { position, className, labelClassName } = props;

  return (
    <div className={className}>
      <Text tag="span" fontWeight="700" fontSize="16" color="light" className={labelClassName}>
        #{position}
      </Text>
    </div>
  );
};

const SportsDetailCard = (props: ISportsDetailCard) => {
  const {
    type,
    imageUrl,
    firstName,
    lastName,
    url,
    mvp = false,
    logoUrl,
    teamName,
    backgroundColor = defaultBackgroundColor,
    points,
    position,
    showPoints,
    role,
    teamUrl,
    label = '',
    lazy = false,
    trackingDataValues,
  } = props;

  const cn = classNames({ type });

  const playerName = firstName ? `${firstName} ${lastName}` : lastName;

  const sizes = { '1x': 220, '2x': 440 };

  const getPlaceHolderURL = (params: { type: sportsDetailCardtypes }) => {
    const { type } = params;
    let placeholderURL = '';

    switch (type) {
      case sportsDetailCardtypes.MARCATORI:
      case sportsDetailCardtypes.PROBABILI_FORMAZIONI:
      case sportsDetailCardtypes.PAGELLE:
        placeholderURL = athletePlaceholderLight;
        break;
      case sportsDetailCardtypes.DRIVERS:
        placeholderURL = athletePlaceholderDark;
        break;
      case sportsDetailCardtypes.TEAMS:
        placeholderURL = carPlaceholderDark;
        break;
      default:
        placeholderURL = athletePlaceholderLight;
    }

    return placeholderURL;
  };

  const playerImage = (
    <ImgPlaceholder
      src={lazy ? undefined : imageUrl}
      data-src={lazy ? imageUrl : undefined}
      placeholder={getPlaceHolderURL({ type })}
      alt={type !== sportsDetailCardtypes.TEAMS ? playerName : teamName}
      title={type !== sportsDetailCardtypes.TEAMS ? playerName : teamName}
      sizes={sizes}
    />
  );

  const specialTypes = [sportsDetailCardtypes.MARCATORI, sportsDetailCardtypes.DRIVERS, sportsDetailCardtypes.TEAMS];

  const styleBackground: CSSProperties = { backgroundColor };
  const teamLogo = <img src={lazy ? undefined : logoUrl} data-src={lazy ? logoUrl : undefined} alt={teamName} />;

  return (
    <div className={cn.item} style={styleBackground}>
      <div className={cn.playerHeaderLeft}>
        {mvp ? <div className={cn.playerBadge}>{t('sports.detail.card.mvp')}</div> : null}
        {position &&
        (type === sportsDetailCardtypes.MARCATORI || sportsDetailCardtypes.DRIVERS || sportsDetailCardtypes.TEAMS) ? (
          <PlayerCardPosition
            position={position}
            className={cn.playerCardPosition}
            labelClassName={cn.playerCardPositionLabel}
          />
        ) : null}
      </div>
      <div className={cn.playerHeaderRight}>
        <Cta
          href={type !== sportsDetailCardtypes.TEAMS ? teamUrl : url}
          aria-label={teamName}
          title={teamName}
          className={cn.team}
          trackingData={trackingDataValues?.playerTeam ? { intcmp: trackingDataValues?.playerTeam } : undefined}
        >
          {teamLogo}
        </Cta>
      </div>
      <div className={cn.player}>
        {url ? <div className={cn.image}>{playerImage}</div> : <div className={cn.image}>{playerImage}</div>}
        <PlayerCardFooter
          type={type}
          firstName={firstName}
          lastName={lastName}
          url={url}
          label={label}
          points={points}
          showPoints={showPoints}
          role={role}
          teamName={specialTypes.includes(type) ? teamName : undefined}
          bgColor={specialTypes.includes(type) ? 'blue' : 'light'}
        />
      </div>
      {url ? (
        <Cta
          href={url}
          title={playerName}
          aria-label={playerName}
          className={cn.fullLink}
          trackingData={trackingDataValues?.playerCard ? { intcmp: trackingDataValues.playerCard } : undefined}
        />
      ) : null}
    </div>
  );
};

export default SportsDetailCard;
